<template>
  <div v-if="isLegacyNumericContest" class="contest-page contest-page-empty"></div>
  <CashmereLongImage v-else-if="useCashmereLongImageIntro" />
  <div v-else-if="contest" class="contest-page">
    <section class="hero">
      <div class="hero-left">
        <p class="logo">{{ contest.logo }}</p>
        <h1>{{ contest.name }}</h1>
        <p class="sub">{{ contest.hero }}</p>
        <div class="actions">
          <a-button type="primary" size="large" @click="goApply">立即报名</a-button>
          <a-button size="large" @click="goReview">进入评审</a-button>
        </div>
      </div>
      <a-card class="countdown" :bordered="false">
        <div class="label">赛事倒计时</div>
        <div class="num">{{ countdownText }}</div>
        <div class="date">报名截止：{{ formatDate(displayDeadline) }}</div>
      </a-card>
    </section>

    <a-card class="anchor-nav" :bordered="false">
      <a href="#detail">大赛详情</a>
      <a href="#process">赛事进程</a>
      <a href="#tracks">赛道介绍</a>
      <a href="#" @click.prevent="goApply">报名入口</a>
    </a-card>

    <a-card id="detail" class="section-card" title="大赛详情">
      <p>{{ contest.sections && contest.sections.detail }}</p>
    </a-card>

    <a-card id="process" class="section-card" title="赛事进程">
      <ul class="process-list">
        <li v-for="item in (contest.sections && contest.sections.process) || []" :key="item">{{ item }}</li>
      </ul>
    </a-card>

    <a-card id="tracks" class="section-card" title="赛道介绍">
      <div class="tracks">
        <a-tag color="blue" v-for="item in (contest.sections && contest.sections.tracks) || []" :key="item">{{ item }}</a-tag>
      </div>
    </a-card>
  </div>
</template>

<script>
import { getContestDetail } from "./mockService";
import { activityDetail } from "@/api/Vote";
import CashmereLongImage from "./CashmereLongImage.vue";
import {
  getContestKey,
  isNumericContestId,
  resolveContestActivityId,
} from "@/utils/contestActivity";

function parseDateTime(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value).trim();
  if (!text) return null;

  const direct = new Date(text.includes("T") ? text : text.replace(" ", "T"));
  if (!Number.isNaN(direct.getTime())) return direct;

  const fallback = new Date(text.replace(/-/g, "/"));
  if (!Number.isNaN(fallback.getTime())) return fallback;

  return null;
}

export default {
  name: "ContestDetail",
  components: {
    CashmereLongImage,
  },
  data() {
    return {
      contestId: "graduation-art",
      contest: null,
      activityMeta: null,
      displayDeadline: "",
      countdownText: "--",
      countdownTimer: null,
    };
  },
  computed: {
    useCashmereLongImageIntro() {
      return this.getContestKeyValue() === "cashmere-design";
    },
    isLegacyNumericContest() {
      return isNumericContestId(this.contestId);
    },
    resolvedActivityId() {
      return resolveContestActivityId(this.contestId) || resolveContestActivityId(this.getContestKeyValue()) || 0;
    },
  },
  mounted() {
    this.loadDetail();
  },
  beforeDestroy() {
    this.clearCountdown();
  },
  watch: {
    "$route.params.contestId"() {
      this.loadDetail();
    },
  },
  methods: {
    getContestKeyValue() {
      return getContestKey(this.contestId);
    },
    getIntroResourceKey() {
      const currentKey = this.getContestKeyValue();
      return currentKey === "cashmere-design" ? "graduation-art" : currentKey;
    },
    extractList(res) {
      if (!res) return [];
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.list)) return res.list;
      if (Array.isArray(res.content)) return res.content;
      if (res.data && Array.isArray(res.data.list)) return res.data.list;
      if (res.data && Array.isArray(res.data.content)) return res.data.content;
      return [];
    },
    getContestKeywords() {
      if (this.contestId === "cashmere-design") {
        return ["cashmere-design", "羊绒", "鄂尔多斯"];
      }
      return ["graduation-art", "毕业", "毕设"];
    },
    async loadActivityMeta() {
      const activityId = this.resolvedActivityId;
      if (!activityId) {
        this.activityMeta = null;
        return null;
      }
      const res = await activityDetail(activityId, { isShow: 0, uid: 0 });
      const target = res && res.data ? res.data : null;
      this.activityMeta = target || null;
      return this.activityMeta;
    },
    async loadDetail() {
      this.clearCountdown();
      this.contestId = this.$route.params.contestId || "graduation-art";

      if (this.isLegacyNumericContest) {
        this.redirectLegacyContestPage();
        return;
      }

      if (this.useCashmereLongImageIntro) {
        this.contest = null;
        this.activityMeta = null;
        this.displayDeadline = "";
        this.countdownText = "--";
        return;
      }

      const base = getContestDetail(this.getIntroResourceKey());
      this.contest = base;
      this.displayDeadline = base.deadline;

      try {
        const activity = await this.loadActivityMeta();
        if (activity && activity.endTime) {
          this.displayDeadline = activity.endTime;
        }
      } catch (e) {
        this.displayDeadline = base.deadline;
      }

      this.startCountdown(this.displayDeadline);
    },
    formatDate(value) {
      const date = parseDateTime(value);
      if (!date) return value || "--";
      return date.toLocaleString("zh-CN", { hour12: false });
    },
    startCountdown(deadline) {
      const tick = () => {
        const remain = this.getRemainMs(deadline);
        if (remain <= 0) {
          this.countdownText = "已截止";
          if (this.contest) this.contest.countdownDays = 0;
          return;
        }

        const totalSeconds = Math.floor(remain / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        this.countdownText = `${days}天 ${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
        if (this.contest) this.contest.countdownDays = days;
      };

      tick();
      this.countdownTimer = setInterval(tick, 1000);
    },
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    getRemainMs(deadline) {
      const date = parseDateTime(deadline);
      if (!date) return 0;
      return date.getTime() - Date.now();
    },
    pad(value) {
      return value < 10 ? `0${value}` : String(value);
    },
    redirectLegacyContestPage() {
      if (!this.resolvedActivityId) {
        this.$router.replace("/");
        return;
      }
      this.$router.replace(`/WorkCompetition/${this.resolvedActivityId}`);
    },
    goApply() {
      this.$router.push(`/ContestApply/${this.contestId}`);
    },
    goReview() {
      this.$router.push(`/ContestReview/${this.contestId}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.contest-page {
  width: 1280px;
  margin: 0 auto;
  padding: 20px 0 40px;
}

.hero {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: linear-gradient(120deg, #dbeafe 0%, #f8fafc 70%);
}

.logo {
  margin: 0;
  font-weight: 700;
  color: #1d4ed8;
}

h1 {
  margin: 10px 0;
}

.sub {
  color: #334155;
  margin-bottom: 16px;
}

.actions {
  display: flex;
  gap: 12px;
}

.countdown {
  text-align: center;
  display: grid;
  align-content: center;
  gap: 8px;
}

.label {
  color: #64748b;
}

.num {
  font-size: 24px;
  font-weight: 700;
  color: #b91c1c;
}

.date {
  color: #475569;
}

.anchor-nav {
  margin-top: 16px;
  display: flex;
  gap: 24px;
  a {
    color: #172554;
  }
}

.section-card {
  margin-top: 16px;
}

.process-list {
  margin: 0;
  padding-left: 18px;
  li {
    margin: 8px 0;
  }
}

.tracks {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
